import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import {
  FaMoneyBillWave,
  FaCheckCircle,
  FaTimesCircle,
  FaHourglassHalf,
  FaSearch,
  FaTimes,
  FaSyncAlt,
  FaCheck,
  FaBan,
  FaFileCsv,
  FaInbox,
  FaExclamationTriangle,
  FaWallet,
  FaCopy,
  FaExternalLinkAlt,
  FaArrowDown,
  FaArrowUp,
  FaPhoneAlt,
  FaEnvelope,
  FaWhatsapp,
  FaUserSlash,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes wdFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes wdFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes wdSlide { from { transform: translateX(100%); } to { transform: none; } }
@keyframes wdZoom { from { opacity: 0; transform: scale(.95); } to { opacity: 1; transform: none; } }
@keyframes wdOut { to { opacity: 0; transform: translateY(-16px) rotate(-2deg); } }
@keyframes wdStamp { 0% { opacity: 0; transform: scale(1.8) rotate(-14deg); } 60% { opacity: 1; transform: scale(.95) rotate(-14deg); } 100% { transform: scale(1) rotate(-14deg); } }
.wd-in { animation: wdFadeUp .5s cubic-bezier(.2,.7,.2,1) both; }
.wd-fade { animation: wdFade .2s ease both; }
.wd-slide { animation: wdSlide .3s cubic-bezier(.2,.8,.2,1) both; }
.wd-zoom { animation: wdZoom .25s cubic-bezier(.2,.8,.2,1) both; }
.wd-out { animation: wdOut .5s ease .35s forwards; }
.wd-stamp { animation: wdStamp .45s cubic-bezier(.2,.8,.2,1) both; }
.wd-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease; }
.wd-press:hover { transform: translateY(-1px); }
.wd-press:active { transform: scale(.96); }
.wd-cheque { transition: transform .25s cubic-bezier(.2,.8,.2,1), box-shadow .25s ease; }
.wd-cheque:hover { transform: translateY(-3px) rotate(-.3deg); box-shadow: 0 20px 36px -20px rgba(15,23,42,.45); }
.wd-guilloche { background-image: repeating-radial-gradient(circle at 100% 0, rgba(16,185,129,.07) 0 2px, transparent 2px 10px); }
.wd-mark { background: #fde68a; color: inherit; border-radius: 3px; padding: 0 1px; }
@media (prefers-reduced-motion: reduce) {
  .wd-in, .wd-fade, .wd-slide, .wd-zoom, .wd-out, .wd-stamp { animation: none; }
  .wd-press:hover, .wd-press:active, .wd-cheque:hover { transform: none; }
}
`;

const CARD = "bg-white dark:bg-secondary-dark-bg rounded-2xl border border-slate-200 dark:border-slate-700";
const INPUT =
  "rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500";


/* ================= helpers ================= */

const DAY = 24 * 60 * 60 * 1000;
const num = (v) => Number(v || 0);
const money = (n) => `₹${num(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const parseAny = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d) => (d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");
const fmtTime = (d) => (d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "");
const waiting = (d) => {
  if (!d) return "";
  const h = Math.floor((Date.now() - d.getTime()) / 3600000);
  if (h < 1) return "Just now";
  if (h < 24) return `${h} hr waiting`;
  const days = Math.floor(h / 24);
  return `${days} day${days > 1 ? "s" : ""} waiting`;
};
const nm = (s) => (s && String(s).trim() ? String(s).trim() : null);

// Amount in words, Indian system (for the cheque)
const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
const two = (n) => (n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]}${n % 10 ? ` ${ONES[n % 10]}` : ""}`);
const three = (n) => `${n >= 100 ? `${ONES[Math.floor(n / 100)]} Hundred${n % 100 ? " " : ""}` : ""}${n % 100 ? two(n % 100) : ""}`;
const inWords = (value) => {
  const v = Math.round(num(value) * 100) / 100;
  let rupees = Math.floor(v);
  const paise = Math.round((v - rupees) * 100);
  if (!rupees && !paise) return "Zero Rupees Only";
  const parts = [];
  const crore = Math.floor(rupees / 10000000);
  rupees %= 10000000;
  const lakh = Math.floor(rupees / 100000);
  rupees %= 100000;
  const thousand = Math.floor(rupees / 1000);
  rupees %= 1000;
  if (crore) parts.push(`${two(crore)} Crore`);
  if (lakh) parts.push(`${two(lakh)} Lakh`);
  if (thousand) parts.push(`${two(thousand)} Thousand`);
  if (rupees) parts.push(three(rupees));
  let out = parts.length ? `${parts.join(" ")} Rupees` : "";
  if (paise) out += `${out ? " and " : ""}${two(paise)} Paise`;
  return `${out} Only`;
};

const Hi = ({ text, q }) => {
  const s = String(text ?? "");
  if (!q || !s) return <>{s}</>;
  const i = s.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{s}</>;
  return (
    <>
      {s.slice(0, i)}
      <mark className="wd-mark">{s.slice(i, i + q.length)}</mark>
      {s.slice(i + q.length)}
    </>
  );
};

const waLink = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return null;
  return `https://wa.me/${digits.length === 10 ? `91${digits}` : digits}`;
};

/* ================= page ================= */

const WithdrawalRequest = ({ initialTab = "pending" }) => {
  const [lists, setLists] = useState({ pending: [], accept: [], cancel: [] });
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(initialTab);
  const [query, setQuery] = useState("");
  const [range, setRange] = useState("all");
  const [sortBy, setSortBy] = useState("oldest");
  const [checked, setChecked] = useState([]);
  const [page, setPage] = useState(1);
  const PAGE = 20;

  const [drawer, setDrawer] = useState(null);
  const [account, setAccount] = useState({}); // user_id -> data | 'loading' | 'error'
  const [confirm, setConfirm] = useState(null); // {type:'accept'|'cancel', list}
  const [working, setWorking] = useState(false);
  const [stamped, setStamped] = useState({}); // id -> 'paid' | 'cancelled'

  const toast = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  /* ----- API (same actions) ----- */

  const post = useCallback(
    async (action, extra = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${baseUrl}/wallet.php`, { method: "POST", body: fd, credentials: "include" });
      return res.json();
    },
    [baseUrl]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a, c] = await Promise.all(["pending", "accept", "cancel"].map((status) => post("withdrawRequestList", { status })));
      setLists({
        pending: p?.withdrawals || [],
        accept: a?.withdrawals || [],
        cancel: c?.withdrawals || [],
      });
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Could not load withdrawal requests" });
    }
    setLoading(false);
  }, [post]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const loadAccount = async (user_id) => {
    if (account[user_id] && account[user_id] !== "error") return;
    setAccount((a) => ({ ...a, [user_id]: "loading" }));
    try {
      const res = await post("getWalletData", { user_id });
      setAccount((a) => ({ ...a, [user_id]: res?.status ? res.data : "error" }));
    } catch (e) {
      setAccount((a) => ({ ...a, [user_id]: "error" }));
    }
  };

  const openDrawer = (r) => {
    setDrawer(r);
    loadAccount(r.user_id);
  };

  const runConfirm = async () => {
    if (!confirm) return;
    const { type, list } = confirm;
    setWorking(true);
    let ok = 0;
    const errors = [];
    for (const r of list) {
      try {
        const res = await post("withdrawAction", { withdraw_id: r.id, wid_action: type });
        if (res?.error !== 0) throw new Error(typeof res?.error === "string" ? res.error : "Failed");
        ok += 1;
        setStamped((s) => ({ ...s, [r.id]: type === "accept" ? "paid" : "cancelled" }));
      } catch (e) {
        errors.push(`${nm(r.name) || r.user_id}: ${e.message}`);
      }
    }
    setWorking(false);
    setConfirm(null);
    setDrawer(null);
    setChecked([]);
    if (ok) {
      toast.current?.show({
        severity: "success",
        summary: type === "accept" ? "Accepted" : "Cancelled",
        detail: type === "accept" ? `${ok} withdrawal${ok > 1 ? "s" : ""} approved and debited from wallet` : `${ok} request${ok > 1 ? "s" : ""} cancelled. User notified.`,
      });
    }
    if (errors.length) toast.current?.show({ severity: "error", summary: "Some failed", detail: errors.slice(0, 3).join(" | "), life: 7000 });
    setTimeout(() => {
      setStamped({});
      fetchAll();
    }, 950);
  };

  /* ----- derived ----- */

  const q = query.trim();

  // total pending per user (a user can ask more than once)
  const pendingByUser = useMemo(() => {
    const m = {};
    lists.pending.forEach((r) => {
      m[r.user_id] = (m[r.user_id] || 0) + num(r.amount);
    });
    return m;
  }, [lists.pending]);

  const stats = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const paidMonth = lists.accept.filter((r) => {
      const d = parseAny(r.date_time);
      return d && d >= monthStart;
    });
    return {
      pendingCount: lists.pending.length,
      pendingAmt: lists.pending.reduce((s, r) => s + num(r.amount), 0),
      short: lists.pending.filter((r) => num(r.amount) > num(r.balance)).length,
      paidCount: lists.accept.length,
      paidAmt: lists.accept.reduce((s, r) => s + num(r.amount), 0),
      paidMonthAmt: paidMonth.reduce((s, r) => s + num(r.amount), 0),
      cancelCount: lists.cancel.length,
      cancelAmt: lists.cancel.reduce((s, r) => s + num(r.amount), 0),
    };
  }, [lists]);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    const now = Date.now();
    let l = (lists[tab] || []).filter((r) => {
      if (range !== "all") {
        const d = parseAny(r.date_time);
        if (!d) return false;
        if (range === "7d" && now - d.getTime() > 7 * DAY) return false;
        if (range === "30d" && now - d.getTime() > 30 * DAY) return false;
      }
      if (!ql) return true;
      return [r.name, r.user_id, r.id, r.amount].map((v) => String(v ?? "").toLowerCase()).some((v) => v.includes(ql));
    });
    l = [...l].sort((a, b) => {
      if (sortBy === "amount") return num(b.amount) - num(a.amount);
      const da = parseAny(a.date_time)?.getTime() || 0;
      const db = parseAny(b.date_time)?.getTime() || 0;
      return sortBy === "oldest" ? da - db : db - da;
    });
    return l;
  }, [lists, tab, q, range, sortBy]);

  useEffect(() => {
    setPage(1);
    setChecked([]);
  }, [tab, q, range, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE, safePage * PAGE);
  const filteredAmt = filtered.reduce((s, r) => s + num(r.amount), 0);
  const checkedList = filtered.filter((r) => checked.includes(r.id));

  const canPay = (r) => num(r.amount) <= num(r.balance);

  useEffect(() => {
    const onKey = (e) => {
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName);
      if (confirm) {
        if (e.key === "Escape" && !working) setConfirm(null);
        return;
      }
      if (drawer) {
        if (e.key === "Escape") setDrawer(null);
        return;
      }
      if (!typing && e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirm, working, drawer]);

  const copyText = (text, label = "Copied") => {
    navigator.clipboard?.writeText(String(text));
    toast.current?.show({ severity: "info", summary: label, detail: String(text), life: 1500 });
  };

  const exportCsv = () => {
    const head = ["Request ID", "User ID", "Name", "Withdrawal", "Balance", "Status", "Date"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = filtered.map((r) => [r.id, r.user_id, nm(r.name) || "Unknown user", num(r.amount), num(r.balance), r.status, r.date_time].map(esc).join(","));
    const csv = "\uFEFF" + [head.map(esc).join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ycnp-withdrawals-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  /* ----- cheque (pending) ----- */

  const cheque = (r, i) => {
    const d = parseAny(r.date_time);
    const bal = num(r.balance);
    const amt = num(r.amount);
    const short = amt > bal;
    const otherPending = (pendingByUser[r.user_id] || 0) - amt;
    const after = bal - amt;
    const sel = checked.includes(r.id);
    const stamp = stamped[r.id];
    return (
      <article
        key={r.id}
        className={`wd-cheque ${stamp ? "wd-out" : "wd-in"} relative overflow-hidden rounded-2xl border bg-gradient-to-br from-emerald-50 via-white to-teal-50 dark:from-slate-800 dark:via-slate-800 dark:to-slate-900 ${sel ? "border-emerald-500 ring-2 ring-emerald-500/30" : short ? "border-rose-300 dark:border-rose-800" : "border-emerald-200 dark:border-slate-700"}`}
        style={{ animationDelay: stamp ? undefined : `${Math.min(i, 10) * 50}ms` }}
      >
        <div className="wd-guilloche absolute inset-0 pointer-events-none" />
        {stamp && (
          <span className={`wd-stamp absolute inset-0 m-auto h-fit w-fit z-10 px-4 py-1.5 rounded-lg border-4 text-2xl font-black uppercase tracking-widest bg-white/70 dark:bg-slate-900/70 ${stamp === "paid" ? "border-emerald-500 text-emerald-600" : "border-rose-500 text-rose-600"}`}>
            {stamp}
          </span>
        )}

        <div className="relative p-5">
          {/* top line */}
          <div className="flex items-start justify-between gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={sel} onChange={() => setChecked((c) => (c.includes(r.id) ? c.filter((x) => x !== r.id) : [...c, r.id]))} className="w-4 h-4 accent-emerald-600" aria-label="Select request" />
              <span className="text-[11px] font-bold tracking-widest text-emerald-800/70 dark:text-emerald-300/70 uppercase">YCNP Payout</span>
            </label>
            <div className="text-right">
              <p className="text-[11px] text-slate-500">No. {r.id}</p>
              <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">{fmtDate(d)}</p>
            </div>
          </div>

          {/* pay to */}
          <div className="mt-4 flex items-end gap-3">
            <span className="text-xs text-slate-500 shrink-0 pb-1">Pay to</span>
            <button type="button" onClick={() => openDrawer(r)} className="flex-1 min-w-0 text-left border-b border-dashed border-slate-300 dark:border-slate-600 pb-1 hover:border-emerald-500">
              <span className={`block text-lg font-semibold truncate ${nm(r.name) ? "text-slate-900 dark:text-white" : "text-slate-400 italic"}`}>
                <Hi text={nm(r.name) || "Unknown user"} q={q} />
              </span>
            </button>
            <div className="shrink-0 rounded-xl border-2 border-slate-800 dark:border-slate-300 px-3 py-1.5 bg-white/80 dark:bg-slate-900/60">
              <p className="text-2xl font-black tabular-nums text-slate-900 dark:text-white">
                <Hi text={money(amt)} q={q} />
              </p>
            </div>
          </div>

          <p className="mt-2 text-xs italic text-slate-600 dark:text-slate-300 border-b border-dashed border-slate-300 dark:border-slate-600 pb-1">{inWords(amt)}</p>

          {/* balance check */}
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-white/70 dark:bg-slate-900/40 p-2">
              <p className="text-[10px] text-slate-500">Wallet balance</p>
              <p className="text-sm font-bold tabular-nums text-slate-900 dark:text-white">{money(bal)}</p>
            </div>
            <div className="rounded-lg bg-white/70 dark:bg-slate-900/40 p-2">
              <p className="text-[10px] text-slate-500">Asking</p>
              <p className="text-sm font-bold tabular-nums text-slate-900 dark:text-white">-{money(amt)}</p>
            </div>
            <div className={`rounded-lg p-2 ${short ? "bg-rose-100 dark:bg-rose-900/30" : "bg-emerald-100/80 dark:bg-emerald-900/30"}`}>
              <p className={`text-[10px] ${short ? "text-rose-700" : "text-emerald-700"}`}>Left after</p>
              <p className={`text-sm font-bold tabular-nums ${short ? "text-rose-700" : "text-emerald-700 dark:text-emerald-300"}`}>{money(after)}</p>
            </div>
          </div>

          {short && (
            <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-rose-600">
              <FaExclamationTriangle size={11} /> Not enough balance. Accepting will fail.
            </p>
          )}
          {!short && otherPending > 0 && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300">
              <FaExclamationTriangle size={11} /> This user has another {money(otherPending)} pending too.
            </p>
          )}

          {/* signature line */}
          <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
            <button type="button" onClick={() => copyText(r.user_id, "User ID copied")} className="inline-flex items-center gap-1 tabular-nums hover:text-slate-800">
              A/c <Hi text={r.user_id} q={q} /> <FaCopy size={8} />
            </button>
            <span className={`inline-flex items-center gap-1 ${d && Date.now() - d.getTime() > DAY ? "text-amber-700 font-semibold" : ""}`}>
              <FaHourglassHalf size={9} /> {waiting(d)}
            </span>
          </div>
        </div>

        <div className="relative grid grid-cols-3 border-t border-emerald-100 dark:border-slate-700 bg-white/60 dark:bg-slate-900/30">
          <button type="button" onClick={() => openDrawer(r)} className="h-11 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-800 transition-colors inline-flex items-center justify-center gap-1.5">
            <FaWallet size={11} /> Check
          </button>
          <button type="button" onClick={() => setConfirm({ type: "cancel", list: [r] })} disabled={!!stamp} className="h-11 text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors inline-flex items-center justify-center gap-1.5 border-x border-emerald-100 dark:border-slate-700">
            <FaBan size={11} /> Cancel
          </button>
          <button type="button" onClick={() => setConfirm({ type: "accept", list: [r] })} disabled={!!stamp || short} className="h-11 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:text-slate-500 transition-colors inline-flex items-center justify-center gap-1.5">
            <FaCheck size={11} /> Accept
          </button>
        </div>
      </article>
    );
  };

  const acc = drawer ? account[drawer.user_id] : null;

  /* ================= render ================= */

  return (
    <div style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}} className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen">
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="wd-in flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">Finance</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Withdrawal Requests</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Pay the user first (UPI or bank), then press Accept. Accept takes the amount out of their wallet.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => navigate("/group/wallet")} className="wd-press inline-flex items-center gap-2 h-9 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200">
            <FaWallet size={12} /> Wallets
          </button>
          <button type="button" onClick={exportCsv} disabled={!filtered.length} className="wd-press inline-flex items-center gap-2 h-9 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200 disabled:opacity-50">
            <FaFileCsv /> Export
          </button>
          <button type="button" onClick={fetchAll} disabled={loading} className="wd-press inline-flex items-center gap-2 h-9 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-60">
            <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Status lanes (tabs with totals) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          { id: "pending", title: "Waiting for payout", count: stats.pendingCount, amount: stats.pendingAmt, note: stats.short ? `${stats.short} with low balance` : "Ready to review", grad: "from-amber-500 to-orange-600", icon: FaHourglassHalf },
          { id: "accept", title: "Paid out", count: stats.paidCount, amount: stats.paidAmt, note: `${money(stats.paidMonthAmt)} this month`, grad: "from-emerald-500 to-teal-600", icon: FaCheckCircle },
          { id: "cancel", title: "Cancelled", count: stats.cancelCount, amount: stats.cancelAmt, note: "Money stayed in wallets", grad: "from-rose-500 to-pink-600", icon: FaTimesCircle },
        ].map((l, i) => {
          const Icon = l.icon;
          const active = tab === l.id;
          return (
            <button
              key={l.id}
              type="button"
              onClick={() => setTab(l.id)}
              className={`wd-in wd-press relative overflow-hidden rounded-2xl p-5 text-left ${active ? `bg-gradient-to-br ${l.grad} text-white shadow-lg` : `${CARD} text-slate-900 dark:text-white hover:border-slate-300`}`}
              style={{ animationDelay: `${i * 70}ms` }}
            >
              {active && <span className="pointer-events-none absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/15 blur-xl" />}
              <div className="relative flex items-center justify-between">
                <span className={`inline-flex items-center gap-2 text-sm font-semibold ${active ? "text-white/90" : "text-slate-500"}`}>
                  <Icon /> {l.title}
                </span>
                <span className={`min-w-[28px] h-7 px-2 rounded-lg text-sm font-bold flex items-center justify-center tabular-nums ${active ? "bg-white/20" : "bg-slate-100 dark:bg-slate-700"}`}>{l.count}</span>
              </div>
              <p className="relative mt-3 text-3xl font-black tabular-nums">{loading && !lists.pending.length && !lists.accept.length ? "…" : money(l.amount)}</p>
              <p className={`relative text-xs ${active ? "text-white/80" : l.id === "pending" && stats.short ? "text-rose-600" : "text-slate-500"}`}>{l.note}</p>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className={`${CARD} wd-in mt-4 p-3 flex flex-wrap items-center gap-3`} style={{ animationDelay: "220ms" }}>
        <div className="relative flex-1 min-w-[240px]">
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
          <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, user ID, request number or amount" className={`${INPUT} w-full h-10 pl-10 pr-10`} />
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
            {query ? (
              <button type="button" onClick={() => setQuery("")} className="p-1.5 text-slate-400 hover:text-slate-700" aria-label="Clear search">
                <FaTimes size={12} />
              </button>
            ) : (
              <kbd className="hidden sm:inline text-xs text-slate-400 border border-slate-200 dark:border-slate-600 rounded px-1.5 py-0.5">/</kbd>
            )}
          </div>
        </div>
        <select value={range} onChange={(e) => setRange(e.target.value)} className={`${INPUT} h-10 px-3`}>
          <option value="all">Any date</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
        </select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={`${INPUT} h-10 px-3`}>
          <option value="oldest">Oldest first</option>
          <option value="newest">Newest first</option>
          <option value="amount">Highest amount</option>
        </select>
        <p className="text-sm text-slate-600 dark:text-slate-300 ml-auto">
          <b>{filtered.length}</b> • <b className="text-emerald-700 dark:text-emerald-400">{money(filteredAmt)}</b>
        </p>
      </div>

      {/* Bulk bar (pending only) */}
      {tab === "pending" && filtered.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mt-4 mb-1">
          <label className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              checked={pageRows.length > 0 && pageRows.every((r) => checked.includes(r.id))}
              onChange={() => {
                const ids = pageRows.map((r) => r.id);
                const all = ids.every((id) => checked.includes(id));
                setChecked((c) => (all ? c.filter((id) => !ids.includes(id)) : Array.from(new Set([...c, ...ids]))));
              }}
              className="w-4 h-4 accent-emerald-600"
            />
            Select page
          </label>
          {checked.length > 0 && (
            <div className="wd-fade flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {checked.length} selected ({money(checkedList.reduce((s, r) => s + num(r.amount), 0))})
              </span>
              <button type="button" onClick={() => setConfirm({ type: "accept", list: checkedList.filter(canPay) })} disabled={!checkedList.some(canPay)} className="wd-press inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-40">
                <FaCheck size={11} /> Accept all
              </button>
              <button type="button" onClick={() => setConfirm({ type: "cancel", list: checkedList })} className="wd-press inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold">
                <FaBan size={11} /> Cancel all
              </button>
              <button type="button" onClick={() => setChecked([])} className="text-sm text-slate-500 hover:underline">
                Clear
              </button>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className="mt-3">
        {loading && !filtered.length ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-72 rounded-2xl bg-slate-200 dark:bg-slate-700 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className={`${CARD} px-5 py-16 text-center`}>
            <FaInbox className="mx-auto text-slate-300" size={36} />
            <p className="mt-3 font-semibold text-slate-700 dark:text-slate-200">
              {q ? `Nothing found for "${q}"` : tab === "pending" ? "No payout is waiting" : tab === "accept" ? "No payouts yet" : "Nothing cancelled"}
            </p>
            {tab === "pending" && !q && <p className="text-sm text-slate-500">New withdrawal requests from users will show up here.</p>}
          </div>
        ) : tab === "pending" ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">{pageRows.map((r, i) => cheque(r, i))}</div>
        ) : (
          <div className={`${CARD} overflow-hidden`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="px-4 py-3 font-medium text-left">Request</th>
                    <th className="px-3 py-3 font-medium text-left">User</th>
                    <th className="px-3 py-3 font-medium text-right">Withdrawal</th>
                    <th className="px-3 py-3 font-medium text-right whitespace-nowrap">Wallet now</th>
                    <th className="px-3 py-3 font-medium text-left">Date</th>
                    <th className="px-3 py-3 pr-4 font-medium text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {pageRows.map((r, i) => {
                    const d = parseAny(r.date_time);
                    const paid = tab === "accept";
                    return (
                      <tr key={r.id} onClick={() => openDrawer(r)} className="wd-in cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors" style={{ animationDelay: `${Math.min(i, 12) * 25}ms` }}>
                        <td className="px-4 py-3 tabular-nums text-slate-500">
                          #<Hi text={r.id} q={q} />
                        </td>
                        <td className="px-3 py-3">
                          <p className={`font-medium ${nm(r.name) ? "text-slate-900 dark:text-white" : "text-slate-400 italic"}`}>
                            <Hi text={nm(r.name) || "Unknown user"} q={q} />
                          </p>
                          <p className="text-xs text-slate-500 tabular-nums">
                            <Hi text={r.user_id} q={q} />
                          </p>
                        </td>
                        <td className={`px-3 py-3 text-right font-bold tabular-nums ${paid ? "text-emerald-700 dark:text-emerald-400" : "text-slate-500 line-through"}`}>{money(r.amount)}</td>
                        <td className="px-3 py-3 text-right tabular-nums text-slate-700 dark:text-slate-200">{money(r.balance)}</td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="text-slate-700 dark:text-slate-200">{fmtDate(d)}</div>
                          <div className="text-xs text-slate-500">{fmtTime(d)}</div>
                        </td>
                        <td className="px-3 py-3 pr-4 text-right">
                          <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${paid ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300"}`}>
                            {paid ? <FaCheckCircle size={10} /> : <FaTimesCircle size={10} />} {paid ? "Paid" : "Cancelled"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {filtered.length > PAGE && (
          <div className="flex items-center justify-center gap-3 mt-5 text-sm">
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} className="wd-press w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg flex items-center justify-center disabled:opacity-40" aria-label="Previous page">
              <FaChevronLeft size={11} />
            </button>
            <span className="tabular-nums text-slate-600 dark:text-slate-300">
              Page <b>{safePage}</b> of {totalPages}
            </span>
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="wd-press w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg flex items-center justify-center disabled:opacity-40" aria-label="Next page">
              <FaChevronRight size={11} />
            </button>
          </div>
        )}
      </div>

      {/* ================= Check drawer ================= */}
      {drawer && (
        <div className="fixed inset-0 flex justify-end" style={{ zIndex: 1100 }}>
          <div className="wd-fade absolute inset-0 bg-slate-900/40" onClick={() => setDrawer(null)} />
          <aside className="wd-slide relative w-full max-w-md h-full bg-white dark:bg-secondary-dark-bg shadow-2xl flex flex-col">
            <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs text-slate-500">Request #{drawer.id}</p>
                <h2 className={`text-lg font-semibold truncate ${nm(drawer.name) ? "text-slate-900 dark:text-white" : "text-slate-400 italic"}`}>{nm(drawer.name) || "Unknown user"}</h2>
                <p className="text-2xl font-black tabular-nums text-emerald-700 dark:text-emerald-400">{money(drawer.amount)}</p>
              </div>
              <button type="button" onClick={() => setDrawer(null)} className="wd-press w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700" aria-label="Close">
                <FaTimes />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {!nm(drawer.name) && (
                <p className="flex items-start gap-2 rounded-xl bg-rose-50 dark:bg-rose-900/20 text-rose-800 dark:text-rose-200 text-sm p-3">
                  <FaUserSlash className="mt-0.5 shrink-0" /> This user account was not found. Do not pay until you confirm who this is.
                </p>
              )}

              {!acc || acc === "loading" ? (
                <div className="space-y-3">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="h-20 rounded-xl bg-slate-100 dark:bg-slate-700 animate-pulse" />
                  ))}
                </div>
              ) : acc === "error" ? (
                <p className="text-sm text-rose-600">
                  Could not load this user's wallet.{" "}
                  <button type="button" onClick={() => loadAccount(drawer.user_id)} className="underline">
                    Try again
                  </button>
                </p>
              ) : (
                (() => {
                  const txns = acc.transactions || [];
                  const credit = txns.filter((t) => t.tnx_type === "credit").reduce((s, t) => s + num(t.amount), 0);
                  const debit = txns.filter((t) => t.tnx_type !== "credit").reduce((s, t) => s + num(t.amount), 0);
                  const phone = acc.user?.phone_num;
                  return (
                    <>
                      <section className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-2 text-sm">
                        <p className="text-xs font-semibold text-slate-500">Contact to pay</p>
                        <p className="flex items-center gap-2 text-slate-800 dark:text-slate-100">
                          <FaPhoneAlt className="text-slate-400" size={11} /> {phone || "-"}
                          {phone && (
                            <button type="button" onClick={() => copyText(phone, "Phone copied")} className="text-slate-400 hover:text-slate-700" aria-label="Copy phone">
                              <FaCopy size={10} />
                            </button>
                          )}
                        </p>
                        <p className="flex items-center gap-2 text-slate-800 dark:text-slate-100 break-all">
                          <FaEnvelope className="text-slate-400 shrink-0" size={11} /> {acc.user?.email_id || "-"}
                        </p>
                        <div className="flex gap-2 pt-1">
                          {waLink(phone) && (
                            <a href={waLink(phone)} target="_blank" rel="noreferrer" className="wd-press inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-semibold">
                              <FaWhatsapp /> Ask for UPI
                            </a>
                          )}
                          <button type="button" onClick={() => navigate(`/wallet-account/${drawer.user_id}`)} className="wd-press inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-200 dark:border-slate-600 text-xs font-semibold text-slate-700 dark:text-slate-200">
                            Full wallet <FaExternalLinkAlt size={9} />
                          </button>
                        </div>
                      </section>

                      <section className="grid grid-cols-3 gap-2 text-center">
                        <div className="rounded-xl bg-slate-50 dark:bg-slate-800/60 p-3">
                          <p className="text-[10px] text-slate-500">Wallet now</p>
                          <p className="font-bold tabular-nums text-slate-900 dark:text-white">{money(acc.wallet?.balance ?? drawer.balance)}</p>
                        </div>
                        <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 p-3">
                          <p className="text-[10px] text-emerald-700">Earned in total</p>
                          <p className="font-bold tabular-nums text-emerald-700 dark:text-emerald-300">{money(credit)}</p>
                        </div>
                        <div className="rounded-xl bg-rose-50 dark:bg-rose-900/20 p-3">
                          <p className="text-[10px] text-rose-700">Taken out</p>
                          <p className="font-bold tabular-nums text-rose-700 dark:text-rose-300">{money(debit)}</p>
                        </div>
                      </section>

                      <section>
                        <p className="text-xs font-semibold text-slate-500 mb-2">Where the money came from (latest first)</p>
                        <ul className="space-y-1.5">
                          {txns.slice(0, 12).map((t) => {
                            const cr = t.tnx_type === "credit";
                            const td = parseAny(t.date_time);
                            return (
                              <li key={t.tnx_id} className="flex items-center gap-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 px-3 py-2">
                                <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${cr ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{cr ? <FaArrowDown size={10} /> : <FaArrowUp size={10} />}</span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs text-slate-800 dark:text-slate-100 truncate" title={t.remark}>
                                    {t.remark || t.tnx_type}
                                  </p>
                                  <p className="text-[10px] text-slate-500">
                                    {fmtDate(td)}, {fmtTime(td)}
                                  </p>
                                </div>
                                <b className={`text-sm tabular-nums ${cr ? "text-emerald-700" : "text-rose-700"}`}>
                                  {cr ? "+" : "-"}
                                  {money(t.amount)}
                                </b>
                              </li>
                            );
                          })}
                          {!txns.length && <li className="text-sm text-slate-500">No transactions found</li>}
                        </ul>
                      </section>
                    </>
                  );
                })()
              )}
            </div>

            {tab === "pending" && drawer.status === "pending" && (
              <div className="grid grid-cols-2 gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                <button type="button" onClick={() => setConfirm({ type: "cancel", list: [drawer] })} className="wd-press h-11 rounded-xl border border-rose-200 dark:border-rose-800 bg-white dark:bg-secondary-dark-bg text-rose-600 text-sm font-semibold inline-flex items-center justify-center gap-2">
                  <FaBan size={11} /> Cancel
                </button>
                <button type="button" onClick={() => setConfirm({ type: "accept", list: [drawer] })} disabled={!canPay(drawer)} className="wd-press h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40">
                  <FaCheck size={11} /> Accept
                </button>
              </div>
            )}
          </aside>
        </div>
      )}

      {/* ================= Confirm ================= */}
      {confirm && confirm.list.length > 0 && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
          <div className="wd-fade absolute inset-0 bg-slate-900/60" onClick={() => !working && setConfirm(null)} />
          <div className="wd-zoom relative w-full max-w-md rounded-2xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
            <span className={`w-12 h-12 rounded-2xl flex items-center justify-center ${confirm.type === "accept" ? "bg-emerald-100 text-emerald-600" : "bg-rose-100 text-rose-600"}`}>
              {confirm.type === "accept" ? <FaMoneyBillWave size={18} /> : <FaExclamationTriangle size={18} />}
            </span>
            <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">
              {confirm.type === "accept"
                ? confirm.list.length === 1
                  ? `Have you paid ${money(confirm.list[0].amount)} to ${nm(confirm.list[0].name) || "this user"}?`
                  : `Accept ${confirm.list.length} payouts?`
                : confirm.list.length === 1
                ? "Cancel this withdrawal?"
                : `Cancel ${confirm.list.length} withdrawals?`}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {confirm.type === "accept"
                ? "Accept only after the money is sent. The amount will be taken out of the user's wallet."
                : "The request is cancelled, the money stays in the wallet and the user gets a notification."}
            </p>
            <div className="mt-4 max-h-40 overflow-y-auto rounded-xl bg-slate-50 dark:bg-slate-800/60 divide-y divide-slate-100 dark:divide-slate-700">
              {confirm.list.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 p-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{nm(r.name) || r.user_id}</p>
                    <p className="text-xs text-slate-500">Balance {money(r.balance)}</p>
                  </div>
                  <span className="text-sm font-bold tabular-nums">{money(r.amount)}</span>
                </div>
              ))}
            </div>
            {confirm.list.length > 1 && (
              <p className="mt-2 text-right text-sm text-slate-600 dark:text-slate-300">
                Total <b className="tabular-nums">{money(confirm.list.reduce((s, r) => s + num(r.amount), 0))}</b>
              </p>
            )}
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setConfirm(null)} disabled={working} className="wd-press flex-1 h-11 rounded-xl border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200">
                Go back
              </button>
              <button
                type="button"
                onClick={runConfirm}
                disabled={working}
                className={`wd-press flex-1 h-11 rounded-xl text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 ${confirm.type === "accept" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}
              >
                {working ? <FaSyncAlt className="animate-spin" size={12} /> : confirm.type === "accept" ? <FaCheck size={12} /> : <FaBan size={12} />}
                {working ? "Please wait..." : confirm.type === "accept" ? "Yes, paid. Accept" : "Yes, cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalRequest;